import * as Sentry from '@sentry/react';

import { redactPii } from './redact';

/**
 * Errores de API que PRESERVAN la causa original.
 *
 * El problema que resuelve
 * -----------------------
 * Todo `utils/` y `redux/` del host repetía este patrón:
 *
 *     try { ... } catch (error) { throw new Error('ERROR_GETTING_MESSAGES'); }
 *
 * La excepción original se tiraba a la basura. A Sentry llegaba un string
 * opaco: sin stack del backend, sin `extensions.code` de GraphQL, sin HTTP
 * status. El resultado son issues como `Error al obtener las categorias`
 * (38 eventos, 10 usuarios en `/chat`) sobre los que literalmente no se
 * puede diagnosticar nada — no se sabe si fue un 500 del backend, un error
 * de permisos, un timeout o una query rota.
 *
 * Este módulo NO cura esos fallos: los vuelve diagnosticables.
 *
 * Cómo se usa
 * -----------
 *     import { apiError } from '../observability/apiError';
 *
 *     } catch (error) {
 *       throw apiError('ERROR_GETTING_MESSAGES', error, {
 *         operation: 'getMessages',
 *         module: 'utils/getMessages',
 *       });
 *     }
 *
 * `apiError()` construye el error, lo reporta a Sentry con tags de operación
 * y fingerprint estable, y lo DEVUELVE para que el caller haga `throw`. Se
 * devuelve en vez de lanzarse desde dentro para que el `throw` siga estando
 * en el archivo del caller: un lector ve el flujo de control sin tener que
 * abrir este módulo.
 *
 * Y el stack apunta al sitio real de la falla — pero eso NO sale gratis por
 * devolver el error: `apiError()` recorta el stack contra sí misma antes de
 * reportar. Sin ese recorte, el frame superior era `at apiError` para los 113
 * sitios y esta línea era falsa (ver `decisions.md` D15).
 *
 * Contrato hacia los callers existentes
 * -------------------------------------
 * `ApiError extends Error` y `error.message === code`. El código que ya hacía
 * `err.message === 'ERROR_GETTING_TAGS'` para mapear a un toast SIGUE
 * funcionando sin cambios. Eso es deliberado: son ~130 sitios y romper el
 * contrato de mensaje habría convertido un fix de observabilidad en una
 * refactorización de UI de riesgo alto.
 *
 * Consolidación de fingerprints
 * -----------------------------
 * Sentry agrupa por stack + mensaje. El mismo código lanzado desde distintas
 * líneas (o desde builds con distinto minificado) se fragmentaba en varios
 * grupos: `ERROR_GETTING_MESSAGES` caía en 3 issues distintos. Fijamos
 * `fingerprint: ['api-error', code, variante]`, donde `variante` es el
 * `graphql_code` o el HTTP status cuando se pueden extraer de la causa.
 *
 * El tercer elemento NO es opcional en el diseño: sin él, un 403 de permisos y
 * un 500 del backend con el mismo código caen en el mismo grupo — justo la
 * distinción que este módulo existe para volver visible. Con él se agrupa por
 * causa real sin volver a fragmentar por línea ni por minificado. La operación
 * concreta sigue siendo un TAG (`api_operation`), consultable dentro del grupo.
 *
 * Doble reporte (importante)
 * --------------------------
 * Varios callers despachan estas acciones SIN `.catch()` — por ejemplo
 * `dispatch(searchCategoriesAction())` en `AgentsView.jsx` y
 * `UsersAction.component.jsx`. Esa promesa rechazada termina como
 * *unhandled rejection*, y el handler global de Sentry la captura por su
 * cuenta. Así es como estos errores llegaban a Sentry hasta hoy.
 *
 * Si además reportamos aquí, el MISMO fallo generaría DOS eventos y se
 * duplicaría el consumo de cuota. Para evitarlo marcamos el error como ya
 * reportado (`REPORTED_FLAG`) y `beforeSend` en `init.js` descarta la copia
 * que venga del handler global (`mechanism.handled === false`). El evento
 * que sobrevive es el nuestro, que es el que trae tags y fingerprint.
 */

/**
 * Marca de "ya lo reportamos nosotros". `Symbol.for` para que la marca
 * sobreviva aunque el módulo se instancie dos veces (host + chunk lazy).
 */
export const REPORTED_FLAG = Symbol.for('iventas.observability.apiErrorReported');

/**
 * Error de API con causa preservada.
 *
 * `message` es intencionalmente el código (no un texto humano) para no
 * romper a los callers que hacen match por `err.message`, y para que el
 * título del issue en Sentry NUNCA contenga texto del backend — que es por
 * donde se filtró PII en el pasado. El texto del backend viaja en la
 * excepción encadenada (`cause`), no en el título.
 */
export class ApiError extends Error {
  /**
   * @param {string} code - código estable, p.ej. `ERROR_GETTING_MESSAGES`
   * @param {Object} [options]
   * @param {unknown} [options.cause] - la excepción original
   * @param {string} [options.operation] - función/acción concreta que falló
   * @param {string} [options.module] - módulo de origen, p.ej. `utils/getMessages`
   */
  constructor(code, options = {}) {
    super(code);

    // `this.name = 'ApiError'` a secas creaba una propiedad PROPIA y
    // ENUMERABLE, y con eso un `ApiError` dejaba de serializarse como `{}`:
    // `JSON.stringify(err)` y `{...err}` empezaban a devolver contenido donde
    // antes no devolvían nada. Hoy no hay consumidor de eso en `apps/host`
    // (verificado por @arturogaro con grep exhaustivo), pero es un cambio de
    // contrato gratuito: `Error.prototype.name` es no-enumerable, así que lo
    // definimos igual y el delta desaparece. Ver `decisions.md` D14.
    Object.defineProperty(this, 'name', {
      value: 'ApiError',
      writable: true,
      configurable: true,
      enumerable: false,
    });

    this.code = code;
    if (options.operation) this.operation = options.operation;
    if (options.module) this.module = options.module;

    // `new Error(msg, { cause })` es ES2022. esbuild NO lo transpila (es
    // semántica de runtime, no sintaxis), así que en navegadores viejos
    // dentro del browserslist del host el `cause` se perdería en silencio.
    // Lo asignamos a mano: no-enumerable para que no aparezca en
    // `JSON.stringify` ni en spreads del estado de Redux.
    if (options.cause !== undefined) {
      Object.defineProperty(this, 'cause', {
        value: options.cause,
        writable: true,
        configurable: true,
        enumerable: false,
      });
    }

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isApiError(value) {
  return value instanceof ApiError;
}

/**
 * ¿El reporte de este error YA se resolvió?
 *
 * La marca significa "de este error ya se decidió qué hacer: no lo vuelvas a
 * reportar". Se pone en los cuatro caminos que terminan la decisión:
 *   1. se capturó (el caso normal),
 *   2. el caller pidió `report: false`,
 *   3. la causa ya venía reportada (anidamiento),
 *   4. se agotó el tope de la ventana (ver `MAX_REPORTS_PER_KEY`).
 *
 * La consumen dos sitios:
 *   - `beforeSend` en `init.js`, para descartar la copia del handler global;
 *   - `reportApiError` sobre `err.cause`, para no capturar dos veces cuando un
 *     sitio migrado atrapa a otro sitio migrado.
 *
 * Los códigos de `EXPECTED_CODES` son la única excepción: NO se marcan, a
 * propósito, para que su comportamiento en Sentry quede idéntico al de hoy.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function wasReportedToSentry(value) {
  return Boolean(value && typeof value === 'object' && value[REPORTED_FLAG] === true);
}

/**
 * Normaliza un código para usarlo como tag y como parte del fingerprint.
 *
 * Los códigos del host no siguen una sola convención: hay `SCREAMING_SNAKE`,
 * `ERROR_getWaStatusAccount`, `ERROR_CLEANING FILTERS` (con espacio) y hasta
 * texto en español (`Error al obtener las categorias`). Usarlos crudos como
 * identificador de agrupación deja una taxonomía incoherente en Sentry.
 *
 * Se normaliza SOLO el tag y el fingerprint. El `message` del error se queda
 * exactamente como estaba, porque es el contrato con la UI.
 *
 * @param {unknown} code
 * @returns {string}
 */
export function normalizeCode(code) {
  const normalized = String(code === undefined || code === null ? '' : code)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quita acentos (forma escapada: en el
    // fuente no deben quedar caracteres combinantes invisibles)
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return normalized || 'UNKNOWN';
}

/**
 * Tope de eventos por (código, operación, variante), por pestaña y por VENTANA
 * DE TIEMPO.
 *
 * Existe porque hay bucles que llaman funciones migradas una vez POR FILA. El
 * caso confirmado es `createClientFromPhone` en `utils/getConversations.js`,
 * que importa un CSV: sin tope, una importación de miles de contactos contra
 * un backend con problemas emite miles de eventos en un solo issue — el patrón
 * de "ofensor de cuota" que ya está documentado en `veredictos/sentry-cuota.md`.
 *
 * Por qué hay VENTANA y no solo un tope por carga de página
 * ---------------------------------------------------------
 * La primera versión contaba por pestaña sin reiniciar nunca. La revisión de
 * la ronda 2 (§7) encontró el efecto colateral: el dashboard es una SPA que un
 * asesor deja abierta todo el día, así que una caída de backend de tres horas
 * se veía como 5 eventos y nada más — el conteo de eventos del issue dejaba de
 * significar algo, y peor, la clave quedaba en silencio total el resto del día.
 *
 * Con la ventana, el tope acota la ráfaga (que es lo que quema cuota) pero la
 * DURACIÓN sigue siendo visible: una caída sostenida emite hasta 5 eventos cada
 * 10 minutos, así que se distingue de un pico aislado. Un bucle de importación
 * de mil filas sigue costando 5 eventos, porque corre en segundos.
 *
 * El tope es además por pestaña, así que el conteo de USUARIOS afectados del
 * issue sigue siendo fiel.
 */
export const MAX_REPORTS_PER_KEY = 5;

/** Ancho de la ventana del tope, en milisegundos. */
export const QUOTA_WINDOW_MS = 10 * 60 * 1000;

const reportCounts = new Map();

/** Solo para tests: reinicia los contadores del tope. */
export function resetApiErrorQuota() {
  reportCounts.clear();
}

function withinQuota(key) {
  const now = Date.now();
  const entry = reportCounts.get(key);

  // Sin registro previo, o la ventana anterior ya expiró → arranca una nueva.
  if (!entry || now - entry.windowStart >= QUOTA_WINDOW_MS) {
    reportCounts.set(key, { count: 1, windowStart: now });
    return true;
  }

  if (entry.count >= MAX_REPORTS_PER_KEY) return false;
  entry.count += 1;
  return true;
}

/**
 * Devuelve al bote una unidad de cuota que se cobró pero no se usó.
 *
 * Hace falta porque `withinQuota` incrementa ANTES de capturar. Si
 * `captureException` tira, sin esto el intento fallido se quedaba cobrado: a las
 * cinco excepciones seguidas la clave entraba en la rama de "cuota agotada", que
 * marca el error y con eso apaga TAMBIÉN la red del handler global. O sea que un
 * Sentry que falla al capturar terminaba haciendo desaparecer el error por
 * completo, en vez de degradar al handler global. Un fallo del reporte no puede
 * convertirse en pérdida del fallo reportado.
 */
function refundQuota(key) {
  const entry = reportCounts.get(key);
  if (entry && entry.count > 0) entry.count -= 1;
}

function markReported(err) {
  try {
    Object.defineProperty(err, REPORTED_FLAG, {
      value: true,
      writable: false,
      configurable: true,
      enumerable: false,
    });
  } catch (_e) {
    // Si el error viene congelado no pasa nada: el peor caso es un evento
    // duplicado, no una excepción nueva dentro del handler de errores.
  }
}

/**
 * Códigos que NO son defectos sino resultados de validación esperados: el
 * usuario escribió un nombre que ya existe. Hoy los callers los atrapan para
 * pintar un mensaje en pantalla, así que NUNCA llegan a Sentry.
 *
 * Si `apiError()` los reportara, este fix — que existe para poder diagnosticar
 * fallos — se volvería una fuente nueva de ruido y de consumo de cuota, justo
 * el problema que ya está documentado en `veredictos/sentry-cuota.md`.
 *
 * Para estos códigos SÍ se preserva la causa (sirve al depurar en local), pero
 * no se captura ni se marca como reportado: el comportamiento en Sentry queda
 * idéntico al de hoy. Si alguno se escapa sin `catch`, el handler global lo
 * sigue viendo, igual que antes.
 *
 * Mismo criterio que `EXPECTED_ERROR_CODES` en `observability/apolloLink.js`.
 */
export const EXPECTED_CODES = new Set([
  // Nombre duplicado o reservado — el usuario escribió algo que ya existe.
  'ROLE_WITH_SAME_NAME_ERROR',
  'ROLE_ALREADY_EXISTS',
  'ERROR_CREATING_TAG_DUPLICATED',
  'ERROR_CREATING_WhatsappTemplates_DUPLICATED',
  // Mismo perfil exacto que `ROLE_WITH_SAME_NAME_ERROR`: se lanza cuando el
  // usuario repite el nombre de un campo o usa uno reservado
  // ('THAT FIELD NAME IS A DEFAULT ONE'). Se me había escapado en la primera
  // pasada; lo cazó la revisión adversarial.
  'ERROR_DATASHEETFIELD_NAME',
]);

/**
 * @param {string} code
 * @returns {boolean}
 */
export function isExpectedCode(code) {
  return EXPECTED_CODES.has(String(code));
}

function firstOf(list) {
  return Array.isArray(list) && list.length > 0 ? list[0] : undefined;
}

/** Tope de caracteres para cualquier texto libre que entre a `extra`. */
export const MAX_EXTRA_TEXT_LENGTH = 500;

/**
 * Redacta PII y recorta. La ÚNICA puerta por la que entra texto ajeno a `extra`.
 *
 * El truncado vive aquí y no en `redact.js` a propósito. `redact.js` es
 * temporal y se borra al rebasear sobre el #230, pero el `piiRedaction.js` del
 * #230 **no exporta ninguna función de truncado**: si el truncado viviera allá,
 * el rebase lo tiraría en silencio y un backend que devuelve una página de
 * error de 40 KB la mandaría entera a Sentry. Lo cazó la revisión de
 * @arturogaro y lo confirmó @diegosiac.
 *
 * Con el truncado aquí, el rebase se reduce a cambiar el import de una línea.
 *
 * @param {unknown} value
 * @returns {unknown} el texto redactado y recortado, o `value` si no era string
 */
export function safeText(value) {
  const redacted = redactPii(value);
  if (typeof redacted !== 'string') return redacted;
  if (redacted.length <= MAX_EXTRA_TEXT_LENGTH) return redacted;
  return `${redacted.slice(0, MAX_EXTRA_TEXT_LENGTH)}…[truncado]`;
}

/**
 * Extrae de la causa lo que de verdad sirve para diagnosticar: código de
 * GraphQL, HTTP status y nombre/mensaje del error original.
 *
 * Cubre las tres formas que produce el host:
 *  - `ApolloError` (`client.query` / `client.mutate`): `graphQLErrors[]`,
 *    `networkError.statusCode`
 *  - error de axios (`src/axios.js`, `src/axiosPayments.js`): `response.status`,
 *    `code` (p.ej. `ECONNABORTED`)
 *  - `Error` pelón de cualquier otra cosa
 *
 * Todo el texto libre pasa por `safeText` —redacción + truncado a 500—, sin
 * una sola puerta sin filtro: el mensaje del backend NO es confiable (precedente de teléfonos embebidos en errores de Prisma).
 *
 * @param {unknown} cause
 * @returns {Object} campos planos, listos para `tags` / `extra` de Sentry
 */
export function describeCause(cause) {
  const details = {};
  if (!cause || typeof cause !== 'object') {
    if (typeof cause === 'string') {
      details.cause_message = safeText(cause);
    }
    return details;
  }

  try {
    if (cause.name) details.cause_name = safeText(String(cause.name));
    if (cause.message) details.cause_message = safeText(cause.message);

    // --- GraphQL (ApolloError) ---
    const gqlError = firstOf(cause.graphQLErrors);
    if (gqlError) {
      const ext = gqlError.extensions || {};
      const gqlCode = ext.code || ext.errorCode;
      if (gqlCode) details.graphql_code = String(gqlCode);
      if (gqlError.message) {
        details.graphql_message = safeText(gqlError.message);
      }
      if (gqlError.path) {
        try {
          details.graphql_path = JSON.stringify(gqlError.path);
        } catch (_e) {
          // path con estructura rara: no vale la pena romper por esto
        }
      }
      if (Array.isArray(cause.graphQLErrors) && cause.graphQLErrors.length > 1) {
        details.graphql_error_count = cause.graphQLErrors.length;
      }
    }

    // --- Network error de Apollo ---
    const net = cause.networkError;
    if (net) {
      details.network_error = true;
      if (net.name) details.network_error_name = String(net.name);
      if (typeof net.statusCode === 'number') details.http_status = net.statusCode;
      if (net.message) {
        details.network_error_message = safeText(net.message);
      }
    }

    // --- axios ---
    if (cause.response && typeof cause.response === 'object') {
      if (typeof cause.response.status === 'number') {
        details.http_status = cause.response.status;
      }
      if (cause.response.statusText) {
        // Pasa por el redactor como TODO el texto libre de esta función. Lo
        // normal es 'Not Found', pero la reason phrase la escribe el servidor:
        // nada impide que un backend meta ahí el detalle del fallo, y este
        // módulo no puede tener una sola puerta sin filtro.
        details.http_status_text = safeText(String(cause.response.statusText));
      }
    }
    // `code` de axios (`ECONNABORTED`, `ERR_NETWORK`) o de un DOMException.
    // Solo si es corto: descarta payloads del backend metidos en `code`.
    if (typeof cause.code === 'string' && cause.code.length <= 64) {
      details.cause_code = safeText(cause.code);
    }
  } catch (_e) {
    // La introspección de la causa nunca debe tirar el handler de errores.
  }

  return details;
}

/**
 * Reporta un `ApiError` a Sentry con tags de operación y fingerprint estable.
 *
 * Hay CUATRO razones para no capturar, y todas existen para que este fix no se
 * convierta en un problema de cuota (el objetivo es diagnosticar, no inflar):
 *
 *  1. **Código de validación esperado** (`EXPECTED_CODES`) → ni se captura ni
 *     se marca. Comportamiento idéntico al de hoy.
 *  2. **`report: false`** → el caller sabe que este error se traga a propósito.
 *     Se usa en el bucle de importación de CSV, donde el fallo por fila es
 *     esperado y hasta hoy nunca llegaba a Sentry.
 *  3. **La causa ya venía reportada** → un sitio migrado atrapó a otro sitio
 *     migrado. Sin esto, un solo fallo produce DOS eventos con fingerprints
 *     distintos (confirmado en `filtersDuck → company` y en
 *     `onboardingDuck → onboarding`). Gana el reporte de ADENTRO, que es el
 *     más cercano a la falla real y el que trae el código más específico.
 *  4. **Tope de sesión agotado** → ver `MAX_REPORTS_PER_KEY`.
 *
 * En los casos 2, 3 y 4 SÍ se marca el error, para que la copia del handler
 * global tampoco se reporte y la supresión sea real.
 *
 * Best-effort: si Sentry no está inicializado o falla, no rompe el flujo.
 *
 * @param {ApiError} err
 * @param {Object} [options]
 * @param {boolean} [options.report=true] - `false` para no reportar a Sentry
 * @returns {ApiError} el mismo error, para poder encadenar
 */
export function reportApiError(err, options = {}) {
  if (!err) return err;

  // (1) Validaciones esperadas: se preserva la causa pero NO se reporta ni se
  //     marca, para no convertir este fix en ruido nuevo. Ver `EXPECTED_CODES`.
  if (isExpectedCode(err.code)) return err;

  // (2) Opt-out explícito del caller.
  if (options.report === false) {
    markReported(err);
    return err;
  }

  // (3) Anidamiento: la causa ya es un ApiError cuyo reporte se resolvió. No
  //     recapturamos — solo propagamos. Un fallo, un evento.
  if (wasReportedToSentry(err.cause)) {
    markReported(err);
    return err;
  }

  // Fuera del `try` para poder devolver la cuota si la captura tira: si se
  // declarara dentro, el `catch` no tendría la clave con la que cobrar.
  let quotaKey;
  try {
    const details = describeCause(err.cause);
    const code = normalizeCode(err.code || err.message);

    // Variante: distingue un 403 de permisos de un 500 del backend dentro del
    // mismo código. Es justo la distinción que este fix existe para hacer
    // visible, así que tiene que estar en el criterio de agrupación y no solo
    // en un tag. Sin causa identificable cae en '-' y agrupa como antes.
    const variant =
      details.graphql_code ||
      (details.http_status !== undefined ? String(details.http_status) : '-');

    // (4) Tope por (código, operación, variante) y por carga de página.
    quotaKey = `${code}|${err.operation || 'unknown'}|${variant}`;
    if (!withinQuota(quotaKey)) {
      markReported(err);
      return err;
    }

    const tags = {
      api_code: code,
      api_operation: String(err.operation || 'unknown'),
      api_module: String(err.module || 'unknown'),
    };
    if (details.graphql_code) tags.graphql_code = details.graphql_code;
    if (details.http_status !== undefined) {
      tags.http_status = String(details.http_status);
    }
    // Permite filtrar en Sentry "errores de API que llegaron sin causa" —
    // o sea, sitios que todavía no se migraron o que atrapan `undefined`.
    tags.api_has_cause = err.cause === undefined ? 'false' : 'true';

    const eventId = Sentry.captureException(err, {
      fingerprint: ['api-error', code, variant],
      tags,
      extra: details,
    });

    // OJO con lo que este `eventId` significa y lo que NO significa.
    //
    // `captureException` devuelve un id GENERADO LOCALMENTE y de forma síncrona,
    // antes de que el evento pase por `beforeSend` y mucho antes de que el
    // transporte lo entregue. O sea que NO es acuse de entrega: es prácticamente
    // siempre truthy. Una versión anterior de este comentario decía que aquí se
    // marcaba "solo si Sentry aceptó el evento" y que un descarte dejaría al
    // handler global como red de seguridad. Eso era falso, y creérselo es peor
    // que no tenerlo: da por cubierta una red que no existe.
    //
    // Se conserva el `if` porque un `undefined` sí distingue el caso de "no hay
    // cliente de Sentry inicializado", donde marcar no aportaría nada.
    //
    // Qué queda expuesto, dicho sin adornos: si el transporte descarta el evento
    // (rate-limit del lado de Sentry), el fallo se pierde, porque la marca ya
    // apagó la copia del handler global. Se asume a propósito — la alternativa
    // es no deduplicar y pagar dos eventos por cada fallo, que es justo el
    // consumo de cuota que este módulo existe para evitar. El muestreo NO entra
    // en la ecuación: los errores no se muestrean (`init.js` no fija
    // `sampleRate`, así que es 1.0; el `tracesSampler` es solo para traces).
    if (eventId) markReported(err);
  } catch (_e) {
    // Nunca dejes que el reporte tire el flujo. Si `captureException` falló, NO
    // marcamos el error: así el handler global todavía puede reportarlo.
    //
    // Y hay que DEVOLVER la cuota que se cobró arriba. Sin esto, cinco
    // excepciones seguidas dejaban la clave en "cuota agotada", rama que sí
    // marca el error y apaga la red del handler global: un Sentry que falla al
    // capturar acababa haciendo desaparecer el error por completo.
    if (quotaKey) refundQuota(quotaKey);
  }
  return err;
}

/**
 * Construye un `ApiError` con la causa preservada, lo reporta a Sentry y lo
 * devuelve para que el caller haga `throw`.
 *
 * @param {string} code - código estable (mismo string que se lanzaba antes)
 * @param {unknown} cause - la excepción original del `catch`
 * @param {Object} [meta]
 * @param {string} [meta.operation] - función/acción que falló
 * @param {string} [meta.module] - módulo de origen
 * @param {boolean} [meta.report=true] - `false` cuando el caller traga el error
 *   a propósito (bucles de importación). Preserva la causa sin reportar.
 * @returns {ApiError}
 */
export function apiError(code, cause, meta = {}) {
  const err = new ApiError(code, {
    cause,
    operation: meta.operation,
    module: meta.module,
  });

  // El `captureStackTrace` del constructor oculta el CONSTRUCTOR, pero no esta
  // FACTORY: el frame superior quedaba siendo `at apiError (apiError.js:…)`
  // para los 113 sitios, y el caller real era el segundo frame. O sea que el
  // docstring de arriba —"el stack apunta al sitio real de la falla"— era falso
  // tal cual estaba escrito. Lo cazó @arturogaro.
  //
  // Volver a recortar aquí, contra `apiError`, deja el frame del caller arriba
  // y vuelve cierta la promesa. En Sentry no cambia la agrupación (el
  // fingerprint es explícito), pero sí cambia lo que lee un humano.
  if (Error.captureStackTrace) {
    Error.captureStackTrace(err, apiError);
  }

  return reportApiError(err, { report: meta.report });
}

export default apiError;
